import { z } from 'zod';
import { IPagination } from '../../../../component';
import { PrimaryMarketPayload } from '../../realtyOffersPrimary/types';

export interface IsDropDownOpenMapper {
  [index: number]: boolean;
}

export interface PosObj {
  x: number;
  y: number;
}

export type PolygonType = google.maps.Polygon | google.maps.Circle | null;

export interface StudiesState {
  studiesList: Study[] | null;
  userInput: string;
  selectedStudy: null | Study;
  isDropdownOpen: IsDropDownOpenMapper;
  pagination: IPagination;
  guideSteps: GuideSteps;
  currentStep: GuideSteps[GuideStepsKeys];
  isDragging: boolean;
  translatePos: PosObj;
  currentProgress: number;
  primaryMarketPayload: PrimaryMarketStudiesPayload | null;
  layoutsValuationPayload?: LayoutValuationPayload | null;
  layoutsValuationPrimaryResponse: LayoutValuationResponse | null;
  layoutsValuationSecondaryResponse: LayoutValuationResponse | null;
  polygon: PolygonType;
  qualifiedAudience: QualifiedAudience | null;
  absorptionAndLiquidityResponsePrimary: AbsorptionAndLiquidityResponse | null;
  absorptionAndLiquidityResponseSecondary: AbsorptionAndLiquidityResponse | null;
  installmentsSimulationPrimary: InstallmentsSimulationResponse | null;
  installmentsSimulationSecondary: InstallmentsSimulationResponse | null;
  studyName: string;
  creationDate: string;
  id: string;
  expandedArea: google.maps.Circle | null;
  transactionedMarketCycle: TransactionedMarketCycle | null;
  initialStep: GuideStepsKeys | null;
  author: string;
  titleSearchInput: string;
}

export type QualifiedAudience = {
  id: string;
  primary_market: number;
  secondary_market?: number;
}[];

export interface Study {
  is_public: boolean;
  name: string;
  id: string;
  author: string;
  created_at: string;
  updated_at: string;
  status: StudiesStatuses;
  coordinates: {
    location: {
      coordinates: number[][];
      radius?: number;
    };
  };
  layoutCharacteristics?: DefaultLayoutFormValue[] | null;
  user_id: string;
  description?: string;
  primary_market?: ILayoutValuation[] | [];
  secondary_market?: ILayoutValuation[] | [];
  vgv_primary_secondary: VGVResponse[] | null;
  perfil_socio: QualifiedAudience | null;
  transactioned_market_cycle: TransactionedMarketCycle | null;
  absorption_liquidity: {
    primary_market: AbsorptionAndLiquidityData[];
    secondary_market?: AbsorptionAndLiquidityData[];
  } | null;
  installmentsSimulationPrimary: InstallmentsSimulationResponse | null;
  installmentsSimulationSecondary: InstallmentsSimulationResponse | null;
}

export interface Step {
  component: React.FC;
  canAdvance: boolean;
  nextStep: GuideStepsKeys;
  previousStep?: GuideStepsKeys;
  hasCustomButtons?: boolean;
  step?: number;
  name: GuideStepsKeys;
}

export interface TransactionedMarketCycle {
  primary_market: number;
  secondary_market: number;
  transacioned_market: number;
}

export interface VGVResponse {
  id: string;
  quantity: number;
  vgv_primary_market: number;
  vgv_secondary_market: number;
}

export type GuideSteps = {
  [Key in GuideStepsKeys]: Step;
};

export interface PrimaryMarketStudiesPayload extends PrimaryMarketPayload {
  includeHeatmap: boolean;
}

export interface VGV {
  numberOfUnits: number;
  primaryMarketVGV: number;
  secondaryMarketVGV?: number;
  id: string;
}

export interface ILayoutValuation {
  id: string;
  area: number;
  pricePerSqMt: number;
  pricePerUnit: number;
  vgv?: VGV;
  isEdited?: boolean;
}

export type LayoutValuationResponse = ILayoutValuation[];

export type LayoutValuationPayload = DefaultLayoutFormValue[];

export type StudiesStatuses = 'in_progress' | 'incomplete' | 'finished';

export type GuideStepsKeys =
  | 'initialState'
  | 'dragAndDropInstructions'
  | 'defineRegion'
  | 'understandPrimaryMarket'
  | 'buildingLayouts'
  | 'layoutsValuationPrimary'
  | 'understandSecondaryMarket'
  | 'layoutsValuationSecondary'
  | 'calculateVGV'
  | 'VGVResponse'
  | 'qualifyTargetAudience'
  | 'calculateInstallmentsPrimary'
  | 'calculateInstallmentsSecondary'
  | 'qualifiedAudienceResponse'
  | 'understandTransactions'
  | 'understandMarketCycle'
  | 'absorptionAndLiquidity'
  | 'absorptionAndLiquidityResponse';

export type AbsorptionAndLiquidityResponse = AbsorptionAndLiquidityData[];

export interface AbsorptionAndLiquidityData {
  id: string;
  absorption: string;
  qualifiedAudience: number;
  liquidity: number;
  conversionRate: number;
  phasingEstimate: number;
  area: string;
}

export const installmentsCalculationItems = z.object({
  unitValue: z.string(),
  downPaymentPercentile: z.string().min(1),
  numberOfInstallments: z.number().min(1),
  monthlyInterestPercentile: z.string().min(1),
});
export const calculateInstallmentsSchema = z.object({
  installments: z.array(installmentsCalculationItems),
});

export type LayoutInstallmentsFormData = z.infer<
  typeof calculateInstallmentsSchema
>;
export type InstallmentsItems = z.infer<typeof installmentsCalculationItems>;

export interface InstallmentsSimulation extends InstallmentsItems {
  id: string;
  installmentValue?: number;
  unitTotalValue: number;
  interestValue: number;
  marketType: 'primary' | 'secondary';
}

export type InstallmentsSimulationResponse = InstallmentsSimulation[];

export const NUMERICAL_SELECTS = ['0', '1', '2', '3', '4+'] as const;

const ValidSelects = z.enum(NUMERICAL_SELECTS);
const ValidRealtyTypeIDS = z.enum(['ap', 'cs', 'comm']);

export const defaultLayoutFormValueSchema = z.object({
  id: z.string(),
  realtyType: ValidRealtyTypeIDS,
  area: z.number().min(1),
  bedrooms: ValidSelects,
  suites: ValidSelects,
  garages: ValidSelects,
});
export const layoutsFormSchema = z.object({
  layouts: z.array(defaultLayoutFormValueSchema),
});

export type DefaultLayoutFormValue = z.infer<
  typeof defaultLayoutFormValueSchema
>;
export type LayoutsFormData = z.infer<typeof layoutsFormSchema>;

export interface CreateStudyPayload {
  name: string;
  description?: string;
  is_public: boolean;
  author: string;
}

export interface PayloadStep1Polygon {
  location: {
    coordinates: number[][];
  };
}

export interface PayloadStep1Circle {
  location: {
    coordinates: number[][];
  };
  radius: number;
}

export interface PayloadStep4 {
  layouts: Array<{
    id: string;
    quantity: number;
  }>;
}

export interface PayloadStep5Array {
  marketType: 'primary' | 'secondary';
  unitValue: string;
  downPaymentPercentile: number;
  numberOfInstallments: number;
  monthlyInterestPercentile: number;
  installmentValue: number;
  unitTotalValue: number;
  interestValue: number;
  id: string | undefined;
}

export interface PayloadStep5 {
  layouts: PayloadStep5Array[];
}

export type PayloadStep1 = PayloadStep1Polygon | PayloadStep1Circle;

export type StudyStepPayloads =
  | PayloadStep1
  | LayoutsFormData
  | PayloadStep4
  | PayloadStep5
  | any;
